/**
 * PlanningVueEquipe — LA vue équipe, unique et partagée par toutes les briques.
 *
 * Refonte planning 21/07 : le PRINCIPE vient de la vue RH (RhPlanningsPanel :
 * 1 ligne/agent, jours en colonnes) mais elle est REFAITE proprement. Défauts
 * corrigés vs RH : cases alignées, cadres colorés par créneau, horaire + poste
 * lisibles, poste principal de la journée mis en avant.
 *
 * Elle est autonome par brique : on lui passe directement les `agents` et
 * `creneaux` du getWeek de la brique (pas de dépendance à l'agrégat RH). Le
 * service est donc implicitement bloqué à la brique courante.
 *
 * Un clic sur une case (agent × jour) ou sur un cadre ouvre l'édition du créneau
 * via `onCreneauClick` (même modale unifiée que la vue individuelle).
 */
import { useState } from 'react';
import type { CreneauGrille } from '@/components/planning/PlanningGrilleUnifiee';

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/** Palette agents (couleurs métier, identiques au service technique). */
const PALETTE_AGENTS: Array<{ value: string; label: string }> = [
  { value: '#1D9E75', label: 'Vert' },
  { value: '#D4537E', label: 'Rose' },
  { value: '#BA7517', label: 'Orange' },
  { value: '#7F77DD', label: 'Violet' },
  { value: '#378ADD', label: 'Bleu' },
  { value: '#639922', label: 'Olive' },
  { value: '#D85A30', label: 'Corail' },
  { value: '#534AB7', label: 'Indigo' },
];

export type VueEquipeAgent = { id: string; fullName: string; couleurMarker?: string | null };

/**
 * Date en YYYY-MM-DD dans le fuseau LOCAL. toISOString() décalerait d'un jour
 * pour les heures du soir en été (Europe/Paris = UTC+2).
 */
function isoDe(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}h${d.getMinutes() ? String(d.getMinutes()).padStart(2, '0') : ''}`;
}

/** Poste principal du jour = le titre qui revient le plus dans les créneaux du jour. */
function postePrincipal(creneaux: CreneauGrille[]): string | null {
  if (creneaux.length === 0) return null;
  const compte = new Map<string, number>();
  for (const c of creneaux) compte.set(c.titre, (compte.get(c.titre) ?? 0) + 1);
  let best: string | null = null; let bestN = 0;
  for (const [t, n] of compte) if (n > bestN) { best = t; bestN = n; }
  return best;
}

export function PlanningVueEquipe({
  days,
  agents,
  creneaux,
  colorOfCreneau,
  onCreneauClick,
  onSetAgentColor,
  onDeclarerAbsence,
}: {
  days: Array<{ label?: string; date: Date }>;
  agents: VueEquipeAgent[];
  creneaux: CreneauGrille[];
  colorOfCreneau: (c: CreneauGrille) => string;
  onCreneauClick: (c: CreneauGrille) => void;
  /** Si fourni, une pastille cliquable permet de choisir la couleur de l'agent. */
  onSetAgentColor?: (agentId: string, couleur: string | null) => void;
  /**
   * Si fourni (manager uniquement), permet de déclarer une absence :
   *  · clic sur une case vide → cet agent, ce jour-là ;
   *  · bouton sur la ligne agent → dates à choisir dans le modal.
   * Le modal gère lui-même le multi-jours (dateDebut/dateFin).
   */
  onDeclarerAbsence?: (agent: VueEquipeAgent, jourISO: string) => void;
}) {
  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const todayKey = new Date().toDateString();

  // Index : agentId → jourIdx → créneaux triés par heure
  const parAgentJour = new Map<string, CreneauGrille[][]>();
  for (const a of agents) parAgentJour.set(a.id, days.map(() => []));
  for (const c of creneaux) {
    const cell = parAgentJour.get(c.agentUserId);
    if (!cell) continue;
    const jourIdx = days.findIndex((d) => sameDay(new Date(c.dateDebut), d.date));
    if (jourIdx >= 0) cell[jourIdx]!.push(c);
  }
  for (const cells of parAgentJour.values())
    for (const list of cells) list.sort((x, y) => new Date(x.dateDebut).getTime() - new Date(y.dateDebut).getTime());

  if (agents.length === 0) {
    return (
      <div className="rounded-r-xl border border-border bg-surface/55 p-6 text-center text-sm text-ink-subtle">
        Aucun agent sur ce service.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-r-xl border border-border bg-surface/55 backdrop-blur-glass shadow-soft">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-r-eyebrow text-ink-subtle">
              <th className="sticky left-0 z-10 bg-surface/80 px-3 py-2.5 font-medium backdrop-blur-glass" style={{ minWidth: 150 }}>Agent</th>
              {days.map((d, i) => {
                const isToday = d.date.toDateString() === todayKey;
                return (
                  <th key={i} className="px-2 py-2.5 text-center font-medium" style={{ minWidth: 120 }}>
                    <span className={isToday ? 'text-ink font-semibold' : ''}>
                      {d.label ?? JOURS[d.date.getDay() === 0 ? 6 : d.date.getDay() - 1]} {d.date.getDate()}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => {
              const cells = parAgentJour.get(agent.id) ?? days.map(() => []);
              return (
                <tr key={agent.id} className="border-b-2 border-border-strong align-top last:border-0">
                  <td className="sticky left-0 z-10 bg-surface/80 px-3 py-2 backdrop-blur-glass">
                    <div className="relative flex items-center gap-2">
                      {onSetAgentColor ? (
                        <button
                          type="button"
                          onClick={() => setPickerOpenFor(pickerOpenFor === agent.id ? null : agent.id)}
                          className="inline-block h-3 w-3 shrink-0 rounded-full border border-black/20 transition-transform hover:scale-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
                          style={{ background: agent.couleurMarker ?? 'var(--ink-subtle)' }}
                          title="Choisir la couleur de l'agent"
                          aria-label={`Couleur de ${agent.fullName}`}
                        />
                      ) : (
                        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: agent.couleurMarker ?? 'var(--ink-subtle)' }} />
                      )}
                      <span className="text-sm font-medium text-ink">{agent.fullName}</span>
                      {onDeclarerAbsence && (
                        <button
                          type="button"
                          onClick={() => onDeclarerAbsence(agent, isoDe(days[0]?.date ?? new Date()))}
                          className="ml-auto shrink-0 rounded-r-sm px-1.5 py-0.5 text-2xs text-ink-dim transition-colors hover:bg-surface hover:text-ink"
                          title={`Déclarer une absence pour ${agent.fullName}`}
                          aria-label={`Déclarer une absence pour ${agent.fullName}`}
                        >
                          Absence
                        </button>
                      )}
                      {onSetAgentColor && pickerOpenFor === agent.id && (
                        <div className="absolute left-0 top-full z-[100] mt-1 rounded-r-lg border border-border bg-surface/95 backdrop-blur-glass p-2 shadow-pop" role="dialog" aria-label="Choisir une couleur">
                          <div className="grid grid-cols-4 gap-1">
                            {PALETTE_AGENTS.map((c) => (
                              <button
                                key={c.value}
                                type="button"
                                onClick={() => { onSetAgentColor(agent.id, c.value); setPickerOpenFor(null); }}
                                className="h-6 w-6 rounded-full border-2 border-transparent transition-all hover:scale-110 hover:border-tech focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
                                style={{ backgroundColor: c.value }}
                                title={c.label}
                                aria-label={c.label}
                              />
                            ))}
                          </div>
                          <button type="button" onClick={() => { onSetAgentColor(agent.id, null); setPickerOpenFor(null); }} className="mt-2 w-full text-2xs text-ink-muted transition-colors hover:text-urgent">Effacer</button>
                        </div>
                      )}
                    </div>
                  </td>
                  {cells.map((jour, i) => {
                    const principal = postePrincipal(jour);
                    return (
                      <td key={i} className="px-1.5 py-1.5 align-top">
                        {jour.length === 0 ? (
                          /* Case vide : cliquable pour déclarer une absence sur
                             cet agent ce jour-là (manager uniquement). */
                          onDeclarerAbsence ? (
                            <button
                              type="button"
                              onClick={() => onDeclarerAbsence(agent, isoDe(days[i]!.date))}
                              className="h-full min-h-[28px] w-full rounded-r-sm text-2xs text-transparent transition-colors hover:bg-surface hover:text-ink-dim"
                              title={`Déclarer une absence — ${agent.fullName}`}
                            >
                              + absence
                            </button>
                          ) : (
                            <div className="h-full min-h-[28px]" />
                          )
                        ) : (
                          <div className="space-y-1">
                            {principal && jour.length > 1 && (
                              <div className="truncate px-0.5 text-[9px] font-semibold uppercase tracking-r-eyebrow text-ink-subtle" title={principal}>
                                {principal}
                              </div>
                            )}
                            {jour.map((c) => {
                              const color = colorOfCreneau(c);
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => onCreneauClick(c)}
                                  className="block w-full overflow-hidden rounded-r-md px-1.5 py-1 text-left text-white shadow-soft transition-opacity hover:brightness-110"
                                  style={{ backgroundColor: color, borderLeft: `3px solid ${color}`, filter: 'brightness(1.05)' }}
                                  title={`${c.titre} · ${hhmm(c.dateDebut)}–${hhmm(c.dateFin)}`}
                                >
                                  <div className="text-[10px] font-semibold leading-tight tabular-nums">{hhmm(c.dateDebut)}–{hhmm(c.dateFin)}</div>
                                  <div className="truncate text-[11px] leading-tight opacity-95">{c.titre}</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
